import { useState, useEffect, useCallback } from 'react';
import { QuoteFormState } from '../types';

export interface UseQuoteChatParams {
  userId: string;
  initialData?: Partial<QuoteFormState> | null;
}

export interface UseQuoteChatReturn {
  chatSessionKey: number;
  aiExplanation: string;
  setAiExplanation: (ex: string) => void;
  budgetReady: boolean;
  setBudgetReady: (ready: boolean) => void;
  resetChat: () => void;
}

export function useQuoteChat({ userId, initialData }: UseQuoteChatParams): UseQuoteChatReturn {
  const [budgetReady, setBudgetReady] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (initialData) return true;
    return localStorage.getItem(`rmax_draft_ready_${userId}`) === 'true';
  });

  const [aiExplanation, setAiExplanation] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem(`rmax_draft_explanation_${userId}`) || '';
  });

  const [chatSessionKey, setChatSessionKey] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(`rmax_draft_ready_${userId}`, budgetReady.toString());
      if (budgetReady && userId === 'guest') {
        localStorage.setItem('rmax_guest_demo_completed', 'true');
      }
    } catch (e) {
      console.error('Error saving draft ready state:', e);
    }
  }, [budgetReady, userId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(`rmax_draft_explanation_${userId}`, aiExplanation);
    } catch (e) {
      console.error('Error saving draft explanation:', e);
    }
  }, [aiExplanation, userId]);

  const resetChat = useCallback(() => {
    if (typeof window !== 'undefined') {
      const keysToClear = [
        `rmax_draft_messages_${userId}`,
        `rmax_draft_ready_${userId}`,
        `rmax_draft_explanation_${userId}`,
        'rmax_last_ai_session'
      ];
      keysToClear.forEach(key => localStorage.removeItem(key));
    }
    setBudgetReady(false);
    setAiExplanation('');
    setChatSessionKey(prev => prev + 1);
  }, [userId]);

  return {
    chatSessionKey,
    aiExplanation,
    setAiExplanation,
    budgetReady,
    setBudgetReady,
    resetChat
  };
}
